import React, { useState } from "react";
import {
  Layout,
  Breadcrumb,
  Spin,
  Table,
  DatePicker,
  Button,
  Card,
  Row,
  Col,
  message,
} from "antd";
import {
  FileExcelOutlined,
  FilePdfOutlined,
  SearchOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import { Content } from "antd/es/layout/layout";
import { Link } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import { APPBACKGROUND_COLOR, MAINAPIURL } from "../../appsettings";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";

// PDF & Excel export functions
import { MonthEndSaleSummaryPDF } from "./month-end-sale-summary-pdf";
import { exportMonthEndSaleSummaryExcel } from "./month-end-sale-summary-excel";
import { PDFDownloadLink } from "@react-pdf/renderer";

const { RangePicker } = DatePicker;

const MonthEndSaleSummary = () => {
  const ApiURL = MAINAPIURL;
  const TOKEN = localStorage.getItem("AUTHCODE");

  const [isLoading, setIsLoading] = useState(false);
  const [salesData, setSalesData] = useState([]);
  const [dateRange, setDateRange] = useState([]);

  const columns = [
    { title: "Year", dataIndex: "sysYear", key: "sysYear", align: "left" },
    { title: "Month", dataIndex: "sysMonth", key: "sysMonth", align: "left" },
    {
      title: "Sales Date",
      dataIndex: "salesDate",
      key: "salesDate",
      align: "left",
      render: (text) => dayjs(text).format("YYYY-MM-DD"),
    },
    { title: "New Starts", dataIndex: "newStarts", key: "newStarts", align: "right" },
    { title: "New Recruits", dataIndex: "newRecruits", key: "newRecruits", align: "right" },
    { title: "New Recruits Orders Count", dataIndex: "dailyNewRecruitsOrderCount", key: "dailyNewRecruitsOrderCount", align: "right" },
    { 
      title: "New Recruits Sales Total", 
      dataIndex: "dailyNewRecruitsSales", 
      key: "dailyNewRecruitsSales", 
      align: "right",
      render: (value) =>
        value?.toLocaleString(undefined, { minimumFractionDigits: 2 }), 
    },
    { title: "Sales Force", dataIndex: "salesForce", key: "salesForce", align: "right" },
    { title: "Inactive Consultants Count", dataIndex: "inactiveConsultants", key: "inactiveConsultants", align: "right" },
    { title: "Reactive Consultants Count", dataIndex: "reactiveConsultants", key: "reactiveConsultants", align: "right" },
    { title: "Orders Placed Consultants Count", dataIndex: "dailyOrderConsultants", key: "dailyOrderConsultants", align: "right" },
    { title: "BP Orders Count", dataIndex: "dailyBPOrderCount", key: "dailyBPOrderCount", align: "right" },
    {
      title: "Daily Sales",
      dataIndex: "dailysales",
      key: "dailysales",
      align: "right",
      render: (value) =>
        value?.toLocaleString(undefined, { minimumFractionDigits: 2 }),
    },
    {
      title: "Cumulative Sales",
      dataIndex: "cumSales",
      key: "cumSales",
      align: "right",
      render: (value) =>
        value?.toLocaleString(undefined, { minimumFractionDigits: 2 }),
    },
    { title: "Daily BP", dataIndex: "dailyBp", key: "dailyBp", align: "right" },
    { title: "Cumulative BP", dataIndex: "cumBp", key: "cumBp", align: "right" },
  ];

  const fetchSalesData = async () => {
    if (!dateRange || dateRange.length !== 2) {
      message.warning("Please select a start and end date.");
      return;
    }

    setIsLoading(true);
    try {
      const headers = { SystemAccessToken: TOKEN };
      const payload = {
        startDate: dayjs(dateRange[0]).format("YYYY-MM-DD"),
        endDate: dayjs(dateRange[1]).format("YYYY-MM-DD"),
      };

      const response = await axios.post(
        `${ApiURL}/reports/sales/dailysalesreportbydaterange`,
        payload,
        { headers }
      );

      setSalesData(response.data || []);
    } catch (error) {
      console.error(error);
      message.error("Failed to fetch sales data");
    } finally {
      setIsLoading(false);
    }
  };

  const clearFilters = () => {
    setDateRange([]);
    setSalesData([]);
    message.info("Filters cleared.");
  };

  return (
    <Spin spinning={isLoading} tip="Processing...">
      <Layout style={{ backgroundColor: APPBACKGROUND_COLOR }}>
        <Headerdiv />
        <Content style={{ padding: "10px" }}>
          {/* --- Breadcrumb --- */}
          <Breadcrumb style={{ margin: "16px 0" }}>
            <Breadcrumb.Item>
              <Link to={"/reports"}>Reports</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>Month End Sale Report</Breadcrumb.Item>
          </Breadcrumb>

          {/* --- Filters --- */}
          <Card title={
            <Row justify="space-between" align="middle">
              <Col>Month End Sale Report</Col>
              <Row gutter={16} justify="end" style={{ marginBottom: 10 }}>
                <Col>
                  <Button
                    type="primary"
                    icon={<FileExcelOutlined />}
                    style={{ backgroundColor: "#28a745", borderColor: "#28a745" }}
                    disabled={salesData.length === 0}
                    onClick={() => exportMonthEndSaleSummaryExcel(salesData, dateRange)}
                  >
                    Export Excel
                  </Button>
                </Col>
                <Col>
                  <PDFDownloadLink
                    document={
                      <MonthEndSaleSummaryPDF data={salesData} dateRange={dateRange} />
                    }
                    fileName={`MonthEndSaleSummary_${dayjs().format("YYYYMMDD_HHmmss")}.pdf`}
                  >
                    {({ loading }) => (
                      <Button
                        type="primary"
                        icon={<FilePdfOutlined />}
                        style={{ backgroundColor: "#dc3545", borderColor: "#dc3545" }}
                        disabled={salesData.length === 0}
                      >
                        {loading ? "Generating PDF..." : "Export PDF"}
                      </Button>
                    )}
                  </PDFDownloadLink>
                </Col>
              </Row>
            </Row>
          } style={{ marginBottom: 20 }}>
            <Row gutter={16} justify="flex-start" align="middle">
              <Col>
                <RangePicker
                  allowClear={false}
                  allowEmpty={false}
                  format="YYYY-MM-DD"
                  onChange={(dates) => setDateRange(dates)}
                  value={dateRange}
                />
              </Col>

              <Col>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={fetchSalesData}
                  loading={isLoading}
                >
                  Get Data
                </Button>
              </Col>

              <Col>
                <Button
                  info
                  icon={<ClearOutlined />}
                  onClick={clearFilters}
                >
                  Clear
                </Button>
              </Col>
            </Row>
          </Card>

          {/* --- Sales Table --- */}
          <Table
            dataSource={salesData}
            columns={columns}
            rowKey={(record, index) => index}
            pagination={{ pageSize: 50 }}
            bordered
            scroll={{ x: "max-content" }}
          />
        </Content>
        <Footerdiv />
      </Layout>
    </Spin>
  );
};

export default MonthEndSaleSummary;
